import { ChangeEvent } from 'react'
import styled from 'styled-components'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'

import { StyledInput } from 'Components/Inputs/StyledInput'
import { normalizeAmountInput, pasteAmountInto } from 'Components/Inputs/FormAmountInput'
import { StyledInputLabel } from 'Components/Inputs/StyledInputLabel'
import { CountryFlagIcon } from 'Components/Icons/CountryFlagIcon'
import { ControlledStyledSelect } from 'Components/Inputs/ControlledStyledSelect'
import { ControlledSwitch } from 'Components/Inputs/Switch/ControlledSwitch'
import { InputContainer } from 'Components/Inputs/ControlledSelect'

import { FlexColumn, FlexRow, spacingMD, spacingLG } from 'Utils/styles/spacing'
import { CountryCode, CountryNames } from 'Utils/types/countries'
import { ChargeFeeType, chargeFeeTypeOptions } from 'Utils/types/employerInfo'
import { isAmount } from 'Utils/helpers/currency'

const CountryFieldContainer = styled(FlexColumn)`
  gap: ${spacingMD};
`

type CountryFormProps<T extends FieldValues> = {
  countryCode: CountryCode
  control: Control<T>
  index?: number
  chargeFeeType?: ChargeFeeType
}

export const CountryForm = <T extends FieldValues>({
  control,
  countryCode,
  index,
  chargeFeeType
}: CountryFormProps<T>) => {
  const hasIndex = index !== undefined
  const fieldName = (field: string) =>
    (hasIndex ? `employerCountries.${index}.${field}` : field) as Path<T>

  return (
    <CountryFieldContainer>
      <CountryFlagIcon text={CountryNames[countryCode]} countryCode={countryCode} />
      <FlexRow gap={spacingLG}>
        <InputContainer>
          <StyledInputLabel>Processing Fee</StyledInputLabel>
          <Controller
            control={control}
            name={fieldName('processingFee')}
            rules={{
              validate: (value) =>
                isAmount(value) ||
                `A valid processing fee for ${CountryNames[countryCode]} is required.`
            }}
            render={({ field: { onChange, value } }) => (
              <StyledInput
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(normalizeAmountInput(e))}
                onPaste={pasteAmountInto(onChange)}
                value={value}
              />
            )}
          />
        </InputContainer>
        <InputContainer>
          <ControlledStyledSelect
            label="Charge Fee Per"
            name={fieldName('chargeFeePer')}
            options={chargeFeeTypeOptions}
            control={control}
            defaultValue={chargeFeeType}
          />
        </InputContainer>
      </FlexRow>
      <FlexRow gap={spacingLG}>
        <InputContainer>
          <StyledInputLabel>Pricing Description</StyledInputLabel>
          <Controller
            control={control}
            name={fieldName('pricingDescription')}
            rules={{
              required: `Pricing description for ${CountryNames[countryCode]} is required.`
            }}
            render={({ field: { onChange, value } }) => (
              <StyledInput onChange={onChange} value={value} />
            )}
          />
        </InputContainer>
        <ControlledSwitch
          label="Alternative Reimbursement"
          name={fieldName('alternativeReimbursement')}
          control={control}
        />
      </FlexRow>
    </CountryFieldContainer>
  )
}
